import { useEffect, useState, useRef } from 'react';
import Header from './Header';
import api from '../api/client';
import './VerifyIdentity.css';

const SLOTS = [
    { key: 'idCardFront', slot: 'id-front', labelKey: 'verify-id-front', label: 'ID card — front', accept: 'image/*' },
    { key: 'idCardBack', slot: 'id-back', labelKey: 'verify-id-back', label: 'ID card — back', accept: 'image/*' },
    { key: 'artistDocument', slot: 'artist-document', labelKey: 'verify-artist-doc', label: 'Proof you are an artist (image or PDF)', accept: 'image/*,application/pdf' },
];

function UploadSlot({ def, value, disabled, uploading, onUpload, t }) {
    const inputRef = useRef(null);
    const isPdf = typeof value === 'string' && value.toLowerCase().includes('.pdf');
    return (
        <div className={`verify-slot ${value ? 'filled' : ''}`}>
            <div className="verify-slot-label">{t(def.labelKey, def.label)}</div>
            <div className="verify-slot-preview" onClick={() => !disabled && inputRef.current?.click()}>
                {uploading ? (
                    <span className="spinner" />
                ) : value ? (
                    isPdf ? (
                        <a href={value} target="_blank" rel="noreferrer" className="verify-pdf-link" onClick={(e) => e.stopPropagation()}>
                            📄 {t('verify-view-pdf', 'View PDF')}
                        </a>
                    ) : (
                        <img src={value} alt="" className="verify-slot-img" />
                    )
                ) : (
                    <span className="verify-slot-plus">+</span>
                )}
            </div>
            <button
                type="button"
                className="verify-slot-btn"
                onClick={() => inputRef.current?.click()}
                disabled={disabled}
            >
                {value ? t('verify-replace', 'Replace') : t('verify-upload', 'Upload')}
            </button>
            <input
                ref={inputRef}
                type="file"
                accept={def.accept}
                hidden
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onUpload(def, file);
                    e.target.value = '';
                }}
            />
        </div>
    );
}

export default function VerifyIdentity({ translations }) {
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploadingSlot, setUploadingSlot] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const t = (key, fallback) => translations?.[key] || fallback;

    useEffect(() => {
        let active = true;
        api.getMyVerification()
            .then((data) => { if (active) setRecord(data); })
            .catch(() => {})
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    const status = record?.status || 'draft';
    const locked = status === 'pending' || status === 'approved';

    const handleUpload = async (def, file) => {
        setError('');
        setUploadingSlot(def.key);
        try {
            const updated = await api.uploadVerificationDoc(def.slot, file);
            setRecord(updated);
        } catch (err) {
            setError(err.message);
        } finally {
            setUploadingSlot(null);
        }
    };

    const allUploaded = SLOTS.every((s) => record?.[s.key]);

    const handleSubmit = async () => {
        setError('');
        setSubmitting(true);
        try {
            const updated = await api.submitVerification();
            setRecord(updated);
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const statusBanner = () => {
        if (status === 'pending') {
            return <div className="verify-banner pending">⏳ {t('verify-pending', 'Your documents are under review. We will let you know once verified.')}</div>;
        }
        if (status === 'approved') {
            return <div className="verify-banner approved">✓ {t('verify-approved', 'Your identity is verified — you are now an ArtSyde artist!')}</div>;
        }
        if (status === 'rejected') {
            return (
                <div className="verify-banner rejected">
                    ✕ {t('verify-rejected', 'Your request was rejected.')}
                    {record?.rejectionReason && <div className="verify-reason">{record.rejectionReason}</div>}
                    <div className="verify-reason-hint">{t('verify-resubmit-hint', 'Update your documents below and submit again.')}</div>
                </div>
            );
        }
        return null;
    };

    return (
        <>
            <Header translations={translations} />
            <div className="verify-container">
                <h1 className="verify-title">{t('verify-title', 'Become a verified artist')}</h1>
                <p className="verify-subtitle">
                    {t('verify-subtitle', 'Upload a photo of your ID card (front and back) and a document proving you are an artist. An admin will review your request.')}
                </p>

                {loading ? (
                    <div className="loading-page"><div className="spinner" /></div>
                ) : (
                    <>
                        {statusBanner()}
                        <div className="verify-slots">
                            {SLOTS.map((def) => (
                                <UploadSlot
                                    key={def.key}
                                    def={def}
                                    value={record?.[def.key]}
                                    disabled={locked || uploadingSlot === def.key}
                                    uploading={uploadingSlot === def.key}
                                    onUpload={handleUpload}
                                    t={t}
                                />
                            ))}
                        </div>
                        {error && <p className="verify-error">{error}</p>}
                        {!locked && (
                            <button
                                className="verify-submit"
                                onClick={handleSubmit}
                                disabled={!allUploaded || submitting}
                            >
                                {submitting
                                    ? t('verify-submitting', 'Submitting...')
                                    : t('verify-submit', 'Submit for review')}
                            </button>
                        )}
                        {!allUploaded && !locked && (
                            <p className="verify-hint">{t('verify-need-all', 'Upload all three documents to submit.')}</p>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
